import React, { createContext, useState, useEffect, useContext, useMemo, useCallback } from 'react';
import { themes as themesData, getTheme } from '../themes/themes';

// Re-export themes so other components can access it
export const themes = themesData;

export const ThemeContext = createContext();

export const ThemeProvider = ({ children }) => {
  const [currentTheme, setCurrentTheme] = useState('default');
  const [isLoading, setIsLoading] = useState(true);
  const lastThemeRef = React.useRef('default');
  const applyingThemeRef = React.useRef(false);

  // Load theme on mount
  useEffect(() => {
    const token = localStorage.getItem('token');
    const savedTheme = token
      ? (localStorage.getItem('brings-to-mind-theme') || 'default')
      : 'default';

    // Apply the theme (only runs once on mount)
    const theme = getTheme(savedTheme);
    const root = document.documentElement;

    // Apply color CSS variables
    Object.entries(theme.colors).forEach(([key, value]) => {
      if (key.startsWith('--')) {
        root.style.setProperty(key, value);
      } else {
        const cssVarName = key.replace(/([A-Z])/g, '-$1').toLowerCase();
        root.style.setProperty(`--color-${cssVarName}`, value);
      }
    });

    // Apply background image
    const backgroundImage = theme.backgroundImage || '/images/waves_gray_abstract.png';
    root.style.setProperty('--background-image', `url('${backgroundImage}')`);

    // Apply typography CSS variables if theme has typography settings
    if (theme.typography) {
      Object.entries(theme.typography).forEach(([key, value]) => {
        const cssVarName = key.replace(/([A-Z])/g, '-$1').toLowerCase();
        root.style.setProperty(`--typography-${cssVarName}`, value);
      });
    } else {
      root.style.removeProperty('--typography-font-family');
      root.style.removeProperty('--typography-heading-weight');
      root.style.removeProperty('--typography-body-weight');
    }

    // Apply image CSS variables
    Object.entries(theme.images).forEach(([key, value]) => {
      if (value) {
        const cssVarName = key.replace(/([A-Z])/g, '-$1').toLowerCase();
        root.style.setProperty(`--image-${cssVarName}`, `url('${value}')`);
      }
    });

    // Store icon for use in components
    root.setAttribute('data-theme-icon', theme.icon);

    setCurrentTheme(savedTheme);
    localStorage.setItem('brings-to-mind-theme', savedTheme);
    lastThemeRef.current = savedTheme;
    setIsLoading(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Empty deps - only run once on mount

  const applyTheme = useCallback((themeName) => {
    // Prevent rapid theme switching - guard against loops
    if (applyingThemeRef.current) {
      console.warn('Theme application already in progress, skipping:', themeName);
      return;
    }

    // Don't apply if it's the same theme
    if (lastThemeRef.current === themeName) {
      return;
    }

    console.log('Applying theme:', themeName, 'from:', lastThemeRef.current);
    applyingThemeRef.current = true;
    lastThemeRef.current = themeName;
    const theme = getTheme(themeName);
    const root = document.documentElement;

    // Apply color CSS variables (includes custom CSS variables like --shadow-sm, --radius-card, etc.)
    Object.entries(theme.colors).forEach(([key, value]) => {
      // Check if it's already a CSS variable (starts with --)
      if (key.startsWith('--')) {
        // Apply directly as CSS variable
        root.style.setProperty(key, value);
      } else {
        // Convert camelCase to kebab-case for CSS variables
        const cssVarName = key.replace(/([A-Z])/g, '-$1').toLowerCase();
        root.style.setProperty(`--color-${cssVarName}`, value);
      }
    });

    // Apply background image - use theme-specific or default to gray waves
    const backgroundImage = theme.backgroundImage || '/images/waves_gray_abstract.png';
    root.style.setProperty('--background-image', `url('${backgroundImage}')`);

    // Apply typography CSS variables if theme has typography settings
    if (theme.typography) {
      Object.entries(theme.typography).forEach(([key, value]) => {
        const cssVarName = key.replace(/([A-Z])/g, '-$1').toLowerCase();
        root.style.setProperty(`--typography-${cssVarName}`, value);
      });
    } else {
      // Reset to default if no typography is specified
      root.style.removeProperty('--typography-font-family');
      root.style.removeProperty('--typography-heading-weight');
      root.style.removeProperty('--typography-body-weight');
    }

    // Apply image CSS variables
    Object.entries(theme.images).forEach(([key, value]) => {
      if (value) {
        const cssVarName = key.replace(/([A-Z])/g, '-$1').toLowerCase();
        root.style.setProperty(`--image-${cssVarName}`, `url('${value}')`);
      }
    });

    // Store icon for use in components
    root.setAttribute('data-theme-icon', theme.icon);

    setCurrentTheme(themeName);
    localStorage.setItem('brings-to-mind-theme', themeName);

    // Reset the applying flag
    applyingThemeRef.current = false;
  }, []);

  // Memoize currentThemeData separately to avoid recreation
  const currentThemeData = useMemo(() => getTheme(currentTheme), [currentTheme]);

  const value = useMemo(() => ({
    currentTheme,
    currentThemeData,
    applyTheme,
    isLoading
  }), [currentTheme, currentThemeData, applyTheme, isLoading]);

  return (
    <ThemeContext.Provider value={value}>
      {children}
    </ThemeContext.Provider>
  );
};

// Custom hook for using theme
export const useTheme = () => {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  return context;
};
